import React, { useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import SendToEhpadModal from "../../SendToEhpadModal";

const thStyle = {
    padding: "8px 10px",
    textAlign: "left",
    fontSize: "11px",
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc"
};

const tdStyle = {
    padding: "8px 10px",
    verticalAlign: "middle",
    borderRight: "1px solid #e2e8f0"
};

const inputStyle = {
    width: "100%",
    padding: "5px 8px",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    fontSize: "13px",
    boxSizing: "border-box",
    background: "white"
};

const PLANIF_OPTIONS = [
    { value: "",            label: "—" },
    { value: "EN_COMMANDE", label: "En commande" },
    { value: "EN_RUPTURE",  label: "En rupture" },
];

const PLANIF_COLORS = {
    "":            { border: "#e2e8f0", bg: "white",   color: "#64748b" },
    EN_COMMANDE:   { border: "#fed7aa", bg: "#fff7ed", color: "#c2410c" },
    EN_RUPTURE:    { border: "#fca5a5", bg: "#fff5f5", color: "#dc2626" },
};

function BillingTimeField({ produit, isBillingValide, onChange }) {
    const isAutomaticallySet = isBillingValide && produit.billingHourTime;
    return (
        <>
            <input
                type="time"
                aria-label="Heure de facturation"
                style={{
                    ...inputStyle,
                    border: isAutomaticallySet ? "1px solid #86efac" : "1px solid #e2e8f0",
                    background: isAutomaticallySet ? "#f0fdf4" : "white"
                }}
                value={produit.billingHourTime || ""}
                onChange={event => onChange("billingHourTime", event.target.value)}
            />
            {isBillingValide && (
                <div style={{ fontSize: "11px", color: "#16a34a", marginTop: 3 }}>
                    Heure définie automatiquement
                </div>
            )}
        </>
    );
}

function RuptureWarningControl({ produit, commande, isEnRupture, onChange, onWarn }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
            <input
                type="checkbox"
                checked={!!produit.ehpadWarnedIfOutOfStock}
                disabled={!isEnRupture}
                onChange={event => onChange("ehpadWarnedIfOutOfStock", event.target.checked)}
                style={{
                    width: 15, height: 15,
                    cursor: isEnRupture ? "pointer" : "not-allowed",
                    accentColor: "#f59e0b",
                    opacity: isEnRupture ? 1 : 0.35
                }}
            />
            {isEnRupture && commande?.ehpad && (
                <button
                    type="button"
                    onClick={onWarn}
                    title="Prévenir l'EHPAD de la rupture"
                    style={{
                        padding: "2px 7px",
                        borderRadius: "5px",
                        border: "1px solid #fca5a5",
                        background: "#fff5f5",
                        color: "#dc2626",
                        cursor: "pointer",
                        fontSize: "11px",
                        fontWeight: 500,
                        whiteSpace: "nowrap"
                    }}
                >
                    Prévenir
                </button>
            )}
        </div>
    );
}

function ProductionValidationButton({ validated, blocked, onChange }) {
    if (validated) {
        return (
            <button
                type="button"
                onClick={() => onChange(false)}
                title="Annuler l'envoi en production"
                style={{
                    padding: "4px 10px",
                    borderRadius: "6px",
                    border: "1px solid #86efac",
                    background: "#f0fdf4",
                    color: "#16a34a",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    whiteSpace: "nowrap"
                }}
            >
                En prod <span style={{ color: "#dc2626", fontWeight: 700 }}>✕</span>
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={() => onChange(true)}
            disabled={blocked}
            title={blocked ? "Impossible d'envoyer en production (en rupture)" : "Envoyer en production"}
            style={{
                padding: "4px 10px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                background: blocked ? "#f8fafc" : "white",
                color: blocked ? "#cbd5e1" : "#475569",
                cursor: blocked ? "not-allowed" : "pointer",
                fontSize: "12px",
                fontWeight: 500,
                whiteSpace: "nowrap"
            }}
        >
            Envoyer
        </button>
    );
}

function RobotCommentRow({ value, onChange }) {
    return (
        <tr>
            <td colSpan={5} style={{ padding: "8px 10px", borderTop: "1px solid #e2e8f0" }}>
                <div style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    marginBottom: "6px"
                }}>
                    Commentaire planification robot
                </div>
                <textarea
                    rows={2}
                    placeholder="Notes ou instructions sur la planification…"
                    style={{
                        ...inputStyle,
                        resize: "vertical",
                        fontFamily: "inherit",
                        lineHeight: 1.5
                    }}
                    value={value || ""}
                    onChange={event => onChange("commentaryPlanificationRobot", event.target.value)}
                />
            </td>
        </tr>
    );
}

export function ProductionPlanningFields({ produit, onChange, commande, showRobotComment = false }) {
    const { selectedPharmacy } = useAuth();
    const [showWarnModal, setShowWarnModal] = useState(false);

    const isBillingValide = !!produit.billingValidated;
    const planifStatut = produit.statut || "";
    const isProductionValidated = !!produit.planificationValidated;
    const isEnRupture = planifStatut === "EN_RUPTURE";
    const isValidationBlocked = isEnRupture;
    const planifColor = PLANIF_COLORS[planifStatut] || PLANIF_COLORS[""];

    const warnEhpadContenu = `Bonjour,\nLe produit ${produit.nom || "[produit]"} est actuellement en rupture, nous vous proposons de le remplacer par []\n\nBien cordialement,\n${selectedPharmacy?.name || ""}`;
    const warnEhpadSujet = `Rupture de stock — ${produit.nom || "produit"}`;

    const handlePlanifChange = (newStatut) => {
        const planificationValidated = newStatut === "EN_COMMANDE";
        onChange({ statut: newStatut, planificationValidated });
    };

    const handleValidationChange = (checked) => {
        onChange({ planificationValidated: checked });
    };

    const handleBillingChange = (checked) => {
        if (checked) {
            const now = new Date();
            const pad = n => String(n).padStart(2, "0");
            onChange({ billingValidated: true, billingHourTime: `${pad(now.getHours())}:${pad(now.getMinutes())}` });
        } else {
            onChange("billingValidated", false);
        }
    };

    return (
        <>
        {showWarnModal && commande && (
            <SendToEhpadModal
                commande={commande}
                initialSujet={warnEhpadSujet}
                initialContenu={warnEhpadContenu}
                onClose={(sent) => {
                    setShowWarnModal(false);
                    if (sent) onChange("ehpadWarnedIfOutOfStock", true);
                }}
            />
        )}
        <div style={{ borderRadius: "8px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Facturation validée</th>
                        <th style={thStyle}>
                            Heure de facturation
                            {isBillingValide && produit.billingHourTime && (
                                <span style={{
                                    marginLeft: 6,
                                    fontSize: "10px",
                                    fontWeight: 500,
                                    color: "#16a34a",
                                    background: "#dcfce7",
                                    padding: "1px 5px",
                                    borderRadius: "999px",
                                    textTransform: "none",
                                    letterSpacing: 0
                                }}>auto</span>
                            )}
                        </th>
                        <th style={thStyle}>Statut planification</th>
                        <th style={{ ...thStyle, width: "160px", textAlign: "center" }}>EHPAD prévenu (si rupture)</th>
                        <th style={{ ...thStyle, borderRight: "none", textAlign: "center" }}>Envoyé en production</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                            <input
                                type="checkbox"
                                checked={isBillingValide}
                                onChange={e => handleBillingChange(e.target.checked)}
                                style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#22c55e" }}
                            />
                        </td>
                        <td style={tdStyle}>
                            <BillingTimeField produit={produit} isBillingValide={isBillingValide} onChange={onChange} />
                        </td>
                        <td style={tdStyle}>
                            <select
                                value={planifStatut}
                                onChange={e => handlePlanifChange(e.target.value)}
                                disabled={isProductionValidated}
                                style={{
                                    padding: "4px 8px",
                                    borderRadius: "6px",
                                    border: `1px solid ${planifColor.border}`,
                                    background: planifColor.bg,
                                    color: planifColor.color,
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    cursor: isProductionValidated ? "not-allowed" : "pointer",
                                    width: "100%",
                                    opacity: isProductionValidated ? 0.5 : 1
                                }}
                            >
                                {PLANIF_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center", width: "160px" }}>
                            <RuptureWarningControl
                                produit={produit}
                                commande={commande}
                                isEnRupture={isEnRupture}
                                onChange={onChange}
                                onWarn={() => setShowWarnModal(true)}
                            />
                        </td>
                        <td style={{ ...tdStyle, borderRight: "none", textAlign: "center" }}>
                            <ProductionValidationButton
                                validated={isProductionValidated}
                                blocked={isValidationBlocked}
                                onChange={handleValidationChange}
                            />
                        </td>
                    </tr>
                    {showRobotComment && (
                        <RobotCommentRow value={produit.commentaryPlanificationRobot} onChange={onChange} />
                    )}
                </tbody>
            </table>
        </div>
        </>
    );
}

export default function EtiquetageFields(props) {
    return <ProductionPlanningFields {...props} />;
}
